// ─────────────────────────────────────────────────────────────────────────────
// Correspondance concepts métier → noms du cube SSAS (dimension / hiérarchie / niveau).
//
// SOURCE UNIQUE : tous les écrans (Top N Dashboard, vues d'analyse, comparaison,
// évolution) construisent leurs requêtes à partir d'ici.
// Pour un autre client (autre cube), seul ce fichier change.
//
// Hiérarchies renommées dans le cube « Maxi-cube » (les clés de membres &[...]
// sont inchangées) : [Vendeur].[Code] → [Vendeur].[Vendeur], [Famille].[Cod Famp]
// → [Famille].[Famille], [Depot].[Cod Dep] → [Depot].[Depot], etc.
// Le Top N Dashboard vérifie ces noms via GET /cube/{cube}/dimensions.
// ─────────────────────────────────────────────────────────────────────────────

export interface DimensionRef {
  dimensionName: string;
  hierarchyName: string;
  levelName: string;
}

const attr = (dimensionName: string, attribute: string): DimensionRef => ({
  dimensionName,
  hierarchyName: attribute,
  levelName: attribute,
});

export const CUBE_DIMENSIONS = {
  /** Libellé affiché : « Nom Prénom » (était le code). */
  vendeur: attr("Vendeur", "Vendeur"),
  marque: attr("Categorie", "Marque"),
  depot: attr("Depot", "Depot"),
  /** Libellé affiché : désignation (était le code). */
  famille: attr("Famille", "Famille"),
  saison: attr("Saison", "Saison"),
  sousfamille: attr("Sousfamille", "Sous-famille"),
  /** Ancien [Couleur].[Nom French]. */
  couleur: attr("Couleur", "Nom couleur"),
  /** Triée par ordre de taille. */
  taille: attr("Taille", "Taille"),
  /** Dimension [Article Parent] inchangée. */
  articleParent: attr("Article Parent", "Desg Art"),
  /** Groupes d'articles (vue « Groupes Article ») — dimension [Article Parent] inchangée. */
  groupeArticle: attr("Article Parent", "Art Parentct2"),
  groupe: attr("Groupe", "Groupe"),
  univers: attr("Univers", "Univers"),
  date: attr("Date", "Date"),
};

export type CubeDimensionKey = keyof typeof CUBE_DIMENSIONS;

/**
 * Mesures de VENTE du cube. ⚠️ [Pttc] et [Qte] (sans suffixe) appartiennent au
 * groupe Achat : un achat n'a pas de vendeur (→ « UNKNOWN ») et ses montants ne
 * sont pas des ventes. Le Top N Dashboard doit utiliser celles-ci.
 */
export const CUBE_MEASURES = {
  /** Chiffre d'affaires TTC des ventes (stocké en négatif dans le cube). */
  caTtc: "Pttc - Vente",
  /** Quantité vendue. */
  qte: "Qte - Vente",
};

// ─────────────────────────────────────────────────────────────────────────────
// Résolution contre les métadonnées réelles du cube
//
// Les hiérarchies ont été renommées dans le cube. Tant qu'une base SSAS n'est pas
// redéployée, elle expose encore les anciens noms. Avant chaque requête, on
// choisit donc le nom (nouveau OU ancien) qui existe réellement dans le cube.
// Cela rend aussi les requêtes sauvegardées avec les anciens noms utilisables.
// ─────────────────────────────────────────────────────────────────────────────

/** Paires [nouveau nom, ancien nom] par dimension. */
const HIERARCHY_ALIASES: Record<string, Array<[string, string]>> = {
  vendeur: [["Vendeur", "Code"]],
  categorie: [["Marque", "Cod Marq"]],
  depot: [["Depot", "Cod Dep"]],
  famille: [["Famille", "Cod Famp"]],
  saison: [["Saison", "Cod Fam"]],
  sousfamille: [["Sous-famille", "Cod Fam"]],
  couleur: [
    ["Couleur", "Code Couleur"],
    ["Couleur de base", "Code Couleur Base"],
    ["Nom couleur", "Nom French"],
  ],
  taille: [["Taille", "Code Taille"]],
  article: [
    ["Article", "Cod Art"],
    ["Code article parent", "Art Parentct2"],
    ["Designation", "Desg Art"],
  ],
};

const norm = (s: string | undefined): string => (s ?? "").trim().replace(/\s+/g, " ").toLowerCase();

/** Autres noms connus pour une hiérarchie (ancien ↔ nouveau). */
function aliasesOf(dimensionName: string, name: string | undefined): string[] {
  const pairs = HIERARCHY_ALIASES[norm(dimensionName)] ?? [];
  const n = norm(name);
  return pairs.flatMap(([a, b]) => (norm(a) === n ? [b] : norm(b) === n ? [a] : []));
}

export interface CubeMetadataDimension {
  name: string;
  hierarchies: Array<{ name: string; levels: unknown[] }>;
}

const levelNamesOf = (levels: unknown[]): string[] =>
  levels
    .map((l) => (typeof l === "string" ? l : (l as { name?: string })?.name))
    .filter((l): l is string => !!l);

/**
 * Renvoie la sélection avec les noms qui existent réellement dans le cube
 * (nom demandé, sinon son alias ancien/nouveau). Inchangée si rien ne correspond :
 * le backend renverra alors une erreur explicite.
 */
export function resolveSelection<
  T extends { dimensionName: string; hierarchyName?: string; levelName?: string },
>(sel: T, dims: CubeMetadataDimension[]): T {
  const dim = dims.find((d) => norm(d.name) === norm(sel.dimensionName));
  if (!dim || !sel.hierarchyName) return sel;

  const candidates = [sel.hierarchyName, ...aliasesOf(dim.name, sel.hierarchyName)];
  for (const candidate of candidates) {
    const hier = dim.hierarchies.find((h) => norm(h.name) === norm(candidate));
    if (!hier) continue;
    const levels = levelNamesOf(hier.levels);
    const wanted = [sel.levelName, candidate, ...aliasesOf(dim.name, sel.levelName)];
    const level =
      levels.find((l) => wanted.some((w) => norm(w) === norm(l))) ??
      levels.filter((l) => l !== "(All)").at(-1) ??
      sel.levelName;
    return { ...sel, dimensionName: dim.name, hierarchyName: hier.name, levelName: level };
  }
  return sel;
}

/** Nom unique MDX de la hiérarchie, ex. « [Famille].[Famille] ». */
export const mdxHierarchy = ({ dimensionName, hierarchyName }: DimensionRef): string =>
  `[${dimensionName}].[${hierarchyName}]`;
